import { motion } from "motion/react";
import deliveryIcon from "figma:asset/6c23885f05aee101962aa2c60b22f5549e138b77.png";

interface NavItem {
  id: string;
  label: string;
  icon: JSX.Element;
}

interface BottomNavigationProps {
  activeTab: string;
  onTabChange: (tabId: string) => void;
  notificationCount?: number;
}

export default function BottomNavigation({
  activeTab,
  onTabChange,
  notificationCount = 3,
}: BottomNavigationProps) {
  const navItems: NavItem[] = [
    {
      id: "overview",
      label: "Overview",
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <rect x="3" y="3" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="2" />
          <rect x="14" y="3" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="2" />
          <rect x="3" y="14" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="2" />
          <rect x="14" y="14" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="2" />
        </svg>
      ),
    },
    {
      id: "tests",
      label: "Tests",
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path
            d="M9 3V4M15 3V4M9 20H15M10 8L9.5 20M14 8L14.5 20M6 8H18C18.5523 8 19 7.55228 19 7V5C19 4.44772 18.5523 4 18 4H6C5.44772 4 5 4.44772 5 5V7C5 7.55228 5.44772 8 6 8Z"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ),
    },
    {
      id: "delivery",
      label: "Delivery",
      icon: (
        <img 
          src={deliveryIcon} 
          alt="Delivery"
          className="delivery-icon"
          style={{ width: "24px", height: "24px", objectFit: "contain" }}
        />
      ),
    },
    {
      id: "reports",
      label: "Reports",
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path
            d="M9 12H15M9 16H15M17 21H7C5.89543 21 5 20.1046 5 19V5C5 3.89543 5.89543 3 7 3H12.5858C12.851 3 13.1054 3.10536 13.2929 3.29289L18.7071 8.70711C18.8946 8.89464 19 9.149 19 9.41421V19C19 20.1046 18.1046 21 17 21Z"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      ),
    },
    {
      id: "more",
      label: "More",
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <circle cx="5" cy="12" r="2" fill="currentColor" />
          <circle cx="12" cy="12" r="2" fill="currentColor" />
          <circle cx="19" cy="12" r="2" fill="currentColor" />
        </svg>
      ),
    },
  ];

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 pointer-events-none">
      {/* Responsive Container */}
      <div className="w-full max-w-md sm:max-w-xl md:max-w-2xl lg:max-w-4xl xl:max-w-6xl mx-auto px-0 sm:px-4 md:px-6">
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.4, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="bg-white/98 backdrop-blur-2xl pointer-events-auto sm:rounded-t-3xl md:mb-0"
          style={{
            boxShadow:
              "0 -4px 6px rgba(0, 0, 0, 0.03), 0 -10px 30px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.8)",
            borderTop: "1px solid rgba(0, 0, 0, 0.04)",
          }}
        >
          {/* Safe Area Bottom Padding for Mobile */}
          <div className="flex items-center justify-around px-2 sm:px-4 md:px-8 lg:px-12 py-2 sm:py-3 md:py-3.5 pb-safe">
            {navItems.map((item) => {
              const isActive = activeTab === item.id;
              return (
                <motion.button
                  key={item.id}
                  onClick={() => onTabChange(item.id)}
                  whileTap={{ scale: 0.95 }}
                  className="relative flex flex-col items-center gap-0.5 sm:gap-1 md:gap-1.5 px-2 sm:px-3 md:px-4 lg:px-6 py-2 rounded-2xl transition-colors min-w-[60px] sm:min-w-[70px] md:min-w-[80px] lg:min-w-[90px]"
                  style={{
                    color: isActive ? "#1B2A5B" : "#717182",
                  }}
                >
                  {isActive && (
                    <motion.div
                      layoutId="activeTab"
                      className="absolute inset-0 rounded-2xl"
                      style={{ backgroundColor: "#1B2A5B08" }}
                      transition={{
                        type: "spring",
                        stiffness: 500,
                        damping: 30,
                      }}
                    />
                  )}
                  {/* Icon Container */}
                  <div className="relative z-10 w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 flex items-center justify-center">
                    <div className="scale-100 sm:scale-110 md:scale-125">
                      {item.icon}
                    </div>
                    {/* Notification Badge */}
                    {item.id === "more" && notificationCount > 0 && (
                      <div
                        className="absolute -top-0.5 -right-0.5 sm:-top-1 sm:-right-1 min-w-[14px] sm:min-w-[16px] md:min-w-[18px] h-3.5 sm:h-4 md:h-[18px] px-0.5 sm:px-1 rounded-full flex items-center justify-center"
                        style={{
                          backgroundColor: "#DC2626",
                          boxShadow: "0 2px 4px rgba(220, 38, 38, 0.3)",
                        }}
                      >
                        <span
                          style={{
                            fontSize: "8px",
                            fontWeight: "700",
                            color: "white",
                          }}
                          className="sm:text-[9px] md:text-[10px]"
                        >
                          {notificationCount > 9 ? "9+" : notificationCount}
                        </span>
                      </div>
                    )}
                  </div>
                  {/* Label */}
                  <div
                    className="relative z-10 text-center"
                    style={{
                      fontSize: "9px",
                      letterSpacing: "0.02em",
                      fontWeight: isActive ? "700" : "600",
                    }}
                  >
                    <span className="sm:text-[10px] md:text-[11px] lg:text-xs">
                      {item.label}
                    </span>
                  </div>
                </motion.button>
              );
            })}
          </div>
        </motion.div>
      </div>
    </div>
  );
}